import { Box, Button, Grid2, IconButton, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
  getAssessment,
  getIntranetAssessment,
  getWarmup,
  submitAnswers,
  getProgress,
  saveProgress,
} from "../services/AssessmentService";
import { openAssignedContent } from "../services/AssignmentService";
import { API_URL } from "../config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ReplayIcon from "@mui/icons-material/Replay";
import { toast } from "react-toastify";
import Loader from "./Loader";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { AchievementModal } from "./AchievementModal";
import { useNavigate } from "react-router-dom";
import TimeDisplay from "./TimeDisplay";
import {
  FillBlankPlayer,
  MatchFollowingPlayer,
  OrderingPlayer,
  isAnswered,
} from "./assessment/QuestionPlayers";

/**
 * Render an answer as readable text, whatever shape it arrives in.
 *
 * `correctAnswer` is a Mixed field with years of history behind it: a plain
 * string, a comma-joined string, an array rebuilt by the grader, and — for
 * descriptive questions — NLP feedback. Rendering an array straight into JSX
 * concatenates its items with no separator ("AcademicsHRFinance…"), and calling
 * `.join` on a null selection throws and takes the whole results screen down.
 * One helper, used everywhere an answer is shown, so neither can happen again.
 */
const formatAnswer = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((entry) => formatAnswer(entry))
      .filter((entry) => entry !== "")
      .join(", ");
  }
  // A legacy comma-joined string: tidy the spacing so it reads like the array
  // form rather than running the options together.
  return String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");
};

/**
 * What the learner actually submitted, as one readable line.
 *
 * Keyed on which FIELD is present rather than on `submittedAnswer.type`: the
 * Answer schema drops `type` when a submission is stored, so anything read back
 * from the database has no type to switch on.
 */
const formatSubmittedAnswer = (submitted) => {
  if (!submitted) return "";
  if (submitted.matchAnswers?.length) {
    return submitted.matchAnswers
      .map((entry) => `${entry.left} → ${entry.right}`)
      .join("; ");
  }
  if (submitted.blankAnswers?.length) {
    return submitted.blankAnswers
      .map((value, index) => `${index + 1}. ${value || "—"}`)
      .join("; ");
  }
  if (submitted.orderedOptions?.length) {
    return submitted.orderedOptions
      .map((value, index) => `${index + 1}. ${value}`)
      .join("; ");
  }
  return formatAnswer(
    submitted.selectedOptions ??
      submitted.selectedOption ??
      submitted.trueFalseAnswer ??
      submitted.yesNoAnswer
  );
};

const newAttemptToken = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

function AssessmentSection({
  selectedModule,
  type = "assessment",
  goBack,
  hideNavigation = "no",
  moduleType,
  // The one assessment to show. Only set for ASSIGNED content, where the plan
  // names an exact assessment (a module can carry several) and the assignment
  // itself is what authorizes opening it.
  assessmentId: exactAssessmentId,
}) {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [value, setValue] = useState("");
  const [change, setChange] = useState(false);
  const [responses, setResponses] = useState({});
  const [assessmentId, setAssessmentid] = useState("");
  const [showAchievements, setShowAchievements] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [markData, setMarkData] = useState({});
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const submittingRef = useRef(false);
  const attemptTokenRef = useRef(null);
  const timeUpSubmittedRef = useRef(false);
  const [assessmentData, setAssessmentData] = useState({});
  const [startAssessment, setStartAssessment] = useState("start");
  // Whether a saved draft was found for this attempt — only changes the
  // "start" screen's button label; the answers/position are already merged
  // into `responses`/`currentQuestionIndex` by the time this flips true.
  const [hasDraft, setHasDraft] = useState(false);
  // Guards autosave from firing with the blank `initialResponses` before the
  // saved draft (if any) has been fetched and merged in — otherwise the very
  // first autosave would overwrite a real draft with empty answers.
  const [draftLoaded, setDraftLoaded] = useState(false);
  // "Are the questions here yet" is a different question from "are there any".
  // Without this the pre-fetch state (no questions, no `active` flag) is
  // indistinguishable from an unpublished assessment, so the learner was shown
  // "coming soon" for the whole round trip — on a slow connection, long enough
  // to read it and give up on content that was about to arrive.
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      setStartAssessment("start");
      setQuestions([]);
      setValue("");
      setMarkData({});
      setFetching(true);
      setHasDraft(false);
      setDraftLoaded(false);
      let response;
      if (type === "warmup") {
        response = await getWarmup(selectedModule._id);
      } else if (exactAssessmentId) {
        const payload = await openAssignedContent("assessment", exactAssessmentId);
        response = payload?.assessment || null;
      } else if (moduleType === "Intranet") {
        response = selectedModule;
      } else {
        response = await getAssessment(selectedModule._id, moduleType);
      }
      if (response) {
        setAssessmentid(response._id);
        setAssessmentData(response);
        setQuestions(response.questions);

        // Initialize responses object with all questions
        const initialResponses = response?.questions?.reduce(
          (acc, question) => {
            acc[question._id] = {
              type: question.type,
              selectedOptions: question.type === "multipleChoice" ? [] : null,
              selectedOption:
                question.type === "singleChoice" ? null : undefined,
              yesNoAnswer: question.type === "yesNo" ? null : undefined,
              trueFalseAnswer:
                question.type === "trueFalse" ? null : undefined,
              descriptiveAnswer:
                question.type === "descriptive" ? "" : undefined,
              matchAnswers:
                question.type === "matchFollowing" ? [] : undefined,
              blankAnswers: question.type === "fillBlank" ? [] : undefined,
              orderedOptions: question.type === "ordering" ? [] : undefined,
              isAnswered: false, // Default flag for unanswered
            };
            return acc;
          },
          {}
        );

        setResponses(initialResponses); // Set initial responses with default structure

        // Resume support: overlay any saved draft on top of the defaults, so
        // a page refresh or navigating away mid-attempt doesn't lose answers.
        // Scoring/analytics never see this — it's a different collection from
        // the graded submission, cleared once the learner actually submits.
        const draft = await getProgress(response._id);
        const draftResponses = draft?.responses || {};
        if (Object.keys(draftResponses).length > 0) {
          setResponses((prev) => ({ ...prev, ...draftResponses }));
          setCurrentQuestionIndex(draft.currentQuestionIndex || 0);
          setHasDraft(true);
        }
      }
      setFetching(false);
      setDraftLoaded(true);
    })();
  }, [selectedModule, exactAssessmentId, change]);

  // Autosave the in-progress attempt while the learner is actively taking it.
  // Debounced so every keystroke/click doesn't fire a request; skipped until
  // the initial draft fetch above has resolved, to avoid a race where this
  // saves blank answers over a real draft before it's been merged in.
  useEffect(() => {
    if (!draftLoaded || startAssessment !== "middle" || !assessmentId) return;
    const timer = setTimeout(() => {
      saveProgress(assessmentId, responses, currentQuestionIndex, attemptTokenRef.current);
    }, 800);
    return () => clearTimeout(timer);
  }, [responses, currentQuestionIndex, draftLoaded, startAssessment, assessmentId]);

  const timeLimitSeconds = Number(assessmentData?.time) > 0 ? Number(assessmentData.time) * 60 : 0;

  // Single clock: drives both the visible countdown and the auto-submit.
  useEffect(() => {
    if (startAssessment !== "middle" || !timeLimitSeconds) return;
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [startAssessment, timeLimitSeconds]);

  useEffect(() => {
    if (
      startAssessment === "middle" &&
      timeLimitSeconds &&
      remainingSeconds === 0 &&
      !timeUpSubmittedRef.current
    ) {
      timeUpSubmittedRef.current = true;
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSeconds, startAssessment, timeLimitSeconds]);

  const beginAttempt = () => {
    attemptTokenRef.current = newAttemptToken();
    timeUpSubmittedRef.current = false;
    setRemainingSeconds(timeLimitSeconds || null);
    setStartAssessment("middle");
  };

  useEffect(() => {
    const currentQuestionId = questions[currentQuestionIndex]?._id;
    if (currentQuestionId) {
      setValue(responses[currentQuestionId]?.descriptiveAnswer || "");
    }
  }, [currentQuestionIndex, responses, questions]);

  const handleMultipleChange = (questionId, option) => {
    setResponses((prev) => {
      const selectedOptions = prev[questionId]?.selectedOptions || [];
      const updatedOptions = selectedOptions.includes(option)
        ? selectedOptions.filter((o) => o !== option)
        : [...selectedOptions, option];

      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          selectedOptions: updatedOptions.length > 0 ? updatedOptions : null,
        },
      };
    });
  };

  const handleSingleChoiceChange = (questionId, option) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: option || null,
      },
    }));
  };

  const handleYesNoChange = (questionId, answer) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        yesNoAnswer: answer || null,
      },
    }));
  };

  const handleTrueFalseChange = (questionId, answer) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        trueFalseAnswer: answer || null,
      },
    }));
  };

  /** Patch one question's response — used by the multi-part players. */
  const setResponseField = (questionId, patch) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], ...patch },
    }));
  };

  const handleDescriptiveChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        descriptiveAnswer:
          value.trim() && value !== "<p><br></p>" ? value : null,
      },
    }));
    setValue(value); // Update local Quill value
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setStartAssessment("loading");

    const answers = Object.entries(responses).map(([questionId, value]) => ({
      questionId,
      ...value,
    }));

    if (!attemptTokenRef.current) attemptTokenRef.current = newAttemptToken();
    const result = await submitAnswers({
      assessmentId: assessmentId,
      answers,
      attemptToken: attemptTokenRef.current,
    }).finally(() => {
      submittingRef.current = false;
    });
    if (result.success) {
      attemptTokenRef.current = null;
      setMarkData(result.data.markData);
      setStartAssessment("finished");
      setHasDraft(false); // Server already deleted the draft on submit.
    } else {
      // Back to the questions rather than stranded on the loader.
      setStartAssessment("middle");
      toast.error(
        result.message || "There was an error submitting the assessment."
      );
    }
  };

  function handleContinue() {
    setChange(!change);
    setCurrentQuestionIndex(0);
    setValue("");
    setMarkData({});
    setStartAssessment("start");
  }

  return (
    <Box
      // Sized by its container rather than by the viewport: this now renders
      // inside the section stage and the warm-up strip, not as a whole page.
      sx={{
        width: "100%",
        background: "transparent",
        overflowX: "hidden",
        pb: 4,
      }}
    >
      {showAchievements && (
        <AchievementModal
          open={showAchievements}
          module={selectedModule.title}
          show={setShowAchievements}
        />
      )}
      {startAssessment !== "middle" && (
        <div
          className="ppt-title"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* The heading is the content's own name. It used to fall through to
              the literal word "warmup" for anything that wasn't a warm-up,
              which is exactly the case a standalone assessment hits. */}
          <Box display={"flex"} alignItems={"flex-end"}>
            {hideNavigation !== "yes" && (
              <IconButton
                onClick={() => goBack("list")}
                size="small"
                style={{ color: "black" }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            <Typography
              style={{
                margin: "0px",
                marginTop: "10px",
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              {selectedModule?.name || selectedModule?.title || "Assessment"}
            </Typography>
          </Box>

          <Button
            onClick={() => setShowAchievements(true)}
            endIcon={<EmojiEventsIcon />}
            variant="contained"
            sx={{
              mr: 2,
              textTransform: "none",
              background: "#2e64b8",
              borderRadius: 3,
            }}
          >
            Achievements
          </Button>
        </div>
      )}
      <Box>
        {startAssessment === "start" && (
          <Box
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Box
              width={"90%"}
              mt={5}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              {assessmentData?.time && (
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <Typography fontSize={"1.5rem"}>Time</Typography>
                  <Typography fontSize={"1.5rem"} color="#2e64b8">
                    {assessmentData?.time} min
                  </Typography>
                </Box>
              )}
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Typography fontSize={"1.5rem"}>Number Of Questions</Typography>
                <Typography fontSize={"1.5rem"} color="#2e64b8">
                  {assessmentData?.questions?.length}
                </Typography>
              </Box>
            </Box>
            <Box class="assessment-container">
              {type !== "warmup" && (
                <div class="typewriter">{selectedModule.title}</div>
              )}
              {/* Warm-ups are always-on practice, so they don't require the
                  `active` publish flag that gates real assessments — a warm-up
                  with questions is playable immediately. */}
              {questions?.length > 0 &&
              (type === "warmup" || assessmentData?.active) ? (
                <>
                  <Box>
                    <Typography my={3} fontSize={"1.5rem"} textAlign={"center"}>
                      {type === "warmup"
                        ? "Prepare to test your knowledge with this warm-up quiz!"
                        : "Get ready for the challenge ahead!"}
                    </Typography>

                    {assessmentData?.guidelines && (
                      <Typography
                        my={3}
                        fontSize={"1.2rem"}
                        textAlign={"center"}
                        dangerouslySetInnerHTML={{
                          __html: assessmentData.guidelines,
                        }}
                      />
                    )}
                  </Box>
                </>
              ) : fetching ? (
                <Typography my={3} fontSize={"1.5rem"}>
                  Loading questions…
                </Typography>
              ) : (
                <Typography my={3} fontSize={"1.5rem"}>
                  {type === "warmup"
                    ? "This quiz is coming soon.........."
                    : "Assessment for this module coming soon ....."}
                </Typography>
              )}
              {questions.length > 0 &&
                (type === "warmup" || assessmentData.active) && (
                <Button
                  endIcon={<ArrowCircleRightIcon />}
                  onClick={beginAttempt}
                  variant="contained"
                  sx={{ textTransform: "none", borderRadius: 5, p: 1.5 }}
                >
                  {hasDraft ? "Resume" : "Practice Now"}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {startAssessment === "middle" && (
          <>
            {timeLimitSeconds > 0 && remainingSeconds !== null && (
              <TimeDisplay remainingSeconds={remainingSeconds} />
            )}
            <Grid2
              container
              py={1}
              px={2}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Grid2
                display={"flex"}
                alignItems={"center"}
                maxWidth={"600px"}
                overflow={"auto"}
                // justifyContent={"center"}
                pb={3}
                mt={3}
                size={12}
              >
                {questions?.length &&
                  questions.map((data, index) => (
                    <Box display={"flex"} alignItems={"center"}>
                      <Box
                        onClick={() => setCurrentQuestionIndex(index)}
                        display={"flex"}
                        bgcolor={
                          isAnswered(responses[data._id])
                            ? "green" // Answered questions in green
                            : currentQuestionIndex === index
                            ? "#2e64b8" // Current question in blue
                            : "" // Default background color
                        }
                        border={"1px solid #2e64b8"}
                        sx={{ cursor: "pointer" }}
                        height={"45px"}
                        width={"45px"}
                        borderRadius={"50%"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        <Typography
                          color={
                            currentQuestionIndex === index ||
                            isAnswered(responses[data._id])
                              ? "white" // Current or answered: white on a filled dot
                              : "#2e64b8" // Default color for unanswered questions
                          }
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: "2px",
                          width: "40px",
                          bgcolor: "#2e64b8",
                          display: questions.length === index + 1 ? "none" : "",
                        }}
                      />
                    </Box>
                  ))}
              </Grid2>
            </Grid2>

            <Grid2
              container
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              pb={10}
            >
              <Grid2 size={{ md: 9, lg: 8, xl: 6, xs: 12 }}>
                {questions.length > 0 && currentQuestion ? (
                  <Box
                    border={"1px solid #dadce0"}
                    my={2}
                    sx={{
                      background: "#ffff",
                      borderRadius: 4,
                    }}
                  >
                    <Box
                      display={"flex"}
                      justifyContent={"end"}
                      px={{ xs: 2 }}
                      pt={2}
                    >
                      <Typography
                        bgcolor={"#dadce0"}
                        fontSize={"14px"}
                        fontWeight={500}
                        height={"35px"}
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        px={1}
                        borderRadius={4}
                        style={{ marginLeft: "10px" }}
                      >
                        Score : {currentQuestion.mark}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "start",
                        borderBottom: "1px solid #dadce0",
                        px: { md: 4, xs: 2 },
                        pb: 3,
                        pt: 1,
                      }}
                    >
                      <Typography
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: "10px",
                          color: "black",
                          fontWeight: "bold",
                          borderRadius: "50%",
                          height: "30px",
                          padding: "10px",
                          width: "30px",
                          border: "1px solid #dadce0",
                        }}
                      >
                        {currentQuestionIndex + 1}/{questions.length}
                      </Typography>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: currentQuestion.text,
                        }}
                      />
                    </Box>

                    {currentQuestion.file && (
                      <Box
                        sx={{
                          p: { md: 4, xs: 2 },
                          borderBottom: "1px solid #dadce0",
                        }}
                        my={1}
                      >
                        <img
                          style={{ objectFit: "contain" }}
                          height={"100%"}
                          width={"100%"}
                          src={`${API_URL}/api/assessment/${currentQuestion.file}`}
                        />
                      </Box>
                    )}

                    {/* Render options or answer fields based on question type */}
                    <Box px={{ md: 4, xs: 2 }} py={1}>
                      {currentQuestion.type === "multipleChoice" &&
                        currentQuestion.options && (
                          <>
                            <Typography my={2} fontSize={"1rem"}>
                              Choose one or more options:
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {currentQuestion.options.map(
                                (option, optIndex) => (
                                  <Box
                                    key={optIndex}
                                    borderRadius={3}
                                    pl={2}
                                    py={2}
                                    display={"flex"}
                                    flexDirection={"column"}
                                    gap={1}
                                    width={"100%"}
                                    bgcolor={"#f8f9fa"}
                                    sx={{
                                      "&:hover": {
                                        boxShadow:
                                          "0px 4px 8px rgba(0, 0, 0, 0.2)",
                                      },
                                    }}
                                  >
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={
                                          responses[
                                            currentQuestion._id
                                          ]?.selectedOptions?.includes(
                                            option.text
                                          ) || false
                                        }
                                        onChange={() =>
                                          handleMultipleChange(
                                            currentQuestion._id,
                                            option.text
                                          )
                                        }
                                      />
                                      {option.text}
                                    </label>
                                    {option.file && (
                                      <img
                                        style={{
                                          height: "150px",
                                          width: "150px",
                                        }}
                                        src={`${API_URL}/api/assessment/${option.file}`}
                                      />
                                    )}
                                  </Box>
                                )
                              )}
                            </Box>
                          </>
                        )}
                      {currentQuestion.type === "singleChoice" &&
                        currentQuestion.options && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography>Choose one option:</Typography>
                            {currentQuestion.options.map((option, optIndex) => (
                              <Box
                                key={optIndex}
                                borderRadius={3}
                                pl={2}
                                py={2}
                                display={"flex"}
                                flexDirection={"column"}
                                gap={1}
                                width={"100%"}
                                bgcolor={"#f8f9fa"}
                                sx={{
                                  "&:hover": {
                                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                                  },
                                }}
                              >
                                <label>
                                  <input
                                    type="radio"
                                    name={`singleChoice-${currentQuestionIndex}`}
                                    checked={
                                      responses[currentQuestion._id]
                                        ?.selectedOption === option.text
                                    }
                                    onChange={() =>
                                      handleSingleChoiceChange(
                                        currentQuestion._id,
                                        option.text
                                      )
                                    }
                                  />
                                  {option.text}
                                </label>
                                {option.file && (
                                  <img
                                    style={{ height: "150px", width: "150px" }}
                                    src={`${API_URL}/api/assessment/${option.file}`}
                                  />
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      {currentQuestion.type === "yesNo" && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <label>Yes/No:</label>
                          <Box
                            borderRadius={3}
                            pl={2}
                            py={2}
                            width={"100%"}
                            bgcolor={"#f8f9fa"}
                            sx={{
                              "&:hover": {
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                              },
                            }}
                          >
                            <label>
                              <input
                                type="radio"
                                name={`yesNo-${currentQuestionIndex}`}
                                checked={
                                  responses[currentQuestion._id]
                                    ?.yesNoAnswer === "yes"
                                }
                                onChange={() =>
                                  handleYesNoChange(currentQuestion._id, "yes")
                                }
                              />
                              Yes
                            </label>
                          </Box>
                          <Box
                            borderRadius={3}
                            pl={2}
                            py={2}
                            width={"100%"}
                            bgcolor={"#f8f9fa"}
                            sx={{
                              "&:hover": {
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                              },
                            }}
                          >
                            <label>
                              <input
                                type="radio"
                                name={`yesNo-${currentQuestionIndex}`}
                                checked={
                                  responses[currentQuestion._id]
                                    ?.yesNoAnswer === "no"
                                }
                                onChange={() =>
                                  handleYesNoChange(currentQuestion._id, "no")
                                }
                              />
                              No
                            </label>
                          </Box>
                        </Box>
                      )}
                      {currentQuestion.type === "trueFalse" && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <label>True/False:</label>
                          <Box
                            borderRadius={3}
                            pl={2}
                            py={2}
                            width={"100%"}
                            bgcolor={"#f8f9fa"}
                            sx={{
                              "&:hover": {
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                              },
                            }}
                          >
                            <label>
                              <input
                                type="radio"
                                name={`trueFalse-${currentQuestionIndex}`}
                                checked={
                                  responses[currentQuestion._id]
                                    ?.trueFalseAnswer === "true"
                                }
                                onChange={() =>
                                  handleTrueFalseChange(
                                    currentQuestion._id,
                                    "true"
                                  )
                                }
                              />
                              True
                            </label>
                          </Box>
                          <Box
                            borderRadius={3}
                            pl={2}
                            py={2}
                            width={"100%"}
                            bgcolor={"#f8f9fa"}
                            sx={{
                              "&:hover": {
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                              },
                            }}
                          >
                            <label>
                              <input
                                type="radio"
                                name={`trueFalse-${currentQuestionIndex}`}
                                checked={
                                  responses[currentQuestion._id]
                                    ?.trueFalseAnswer === "false"
                                }
                                onChange={() =>
                                  handleTrueFalseChange(
                                    currentQuestion._id,
                                    "false"
                                  )
                                }
                              />
                              False
                            </label>
                          </Box>
                        </Box>
                      )}
                      {currentQuestion.type === "matchFollowing" && (
                        <MatchFollowingPlayer
                          question={currentQuestion}
                          response={responses[currentQuestion._id]}
                          onChange={(matchAnswers) =>
                            setResponseField(currentQuestion._id, {
                              matchAnswers,
                            })
                          }
                        />
                      )}
                      {currentQuestion.type === "fillBlank" && (
                        <FillBlankPlayer
                          question={currentQuestion}
                          response={responses[currentQuestion._id]}
                          onChange={(blankAnswers) =>
                            setResponseField(currentQuestion._id, {
                              blankAnswers,
                            })
                          }
                        />
                      )}
                      {currentQuestion.type === "ordering" && (
                        <OrderingPlayer
                          question={currentQuestion}
                          response={responses[currentQuestion._id]}
                          onChange={(orderedOptions) =>
                            setResponseField(currentQuestion._id, {
                              orderedOptions,
                            })
                          }
                        />
                      )}
                      {currentQuestion.type === "descriptive" && (
                        <>
                          <Typography my={1}>Your Answer:</Typography>
                          <Box
                            borderRadius={3}
                            pl={2}
                            py={2}
                            width={"100%"}
                            bgcolor={"#f8f9fa"}
                            sx={{
                              "&:hover": {
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                              },
                            }}
                          >
                            <Box pr={2}>
                              <ReactQuill
                                theme="snow"
                                value={value}
                                onChange={(value) =>
                                  handleDescriptiveChange(
                                    currentQuestion._id,
                                    value
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Next and Previous Buttons */}
                    <Box
                      sx={{ p: { md: 2, xs: 2 } }}
                      display={"flex"}
                      justifyContent={"end"}
                      gap={1}
                      mt={2}
                    >
                      <IconButton
                        onClick={handlePrevious}
                        sx={{
                          border: "1px solid #1b6ef3",
                          color: "#1b6ef3",
                          display: currentQuestionIndex === 0 ? "none" : "flex",
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      {currentQuestionIndex === questions.length - 1 ? (
                        <Button
                          sx={{
                            background: "#1b6ef3",
                            color: "white",
                            borderRadius: 4,
                            px: 2,
                          }}
                          onClick={handleSubmit}
                        >
                          Submit
                        </Button>
                      ) : (
                        <IconButton
                          onClick={handleNext}
                          sx={{ border: "1px solid  ", color: "#1b6ef3" }}
                          disabled={
                            currentQuestionIndex === questions.length - 1
                          }
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box border={"1px solid #dadce0"} borderRadius={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "60vh",
                        color: "grey",
                      }}
                    >
                      <h5>No Questions to show</h5>
                    </div>
                  </Box>
                )}
              </Grid2>
            </Grid2>
          </>
        )}
        {startAssessment === "loading" && <Loader />}

        {startAssessment === "finished" && (
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={1}
            justifyContent={"center"}
            minHeight={"80vh"}
            width={"100%"}
            alignItems={"center"}
          >
            <Box
              borderRadius={5}
              py={5}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              flexDirection={"column"}
              gap={3}
              sx={{
                background:
                  "linear-gradient(180deg, #75f 0%, #6943ff 0.01%, #2f2ce9 100%)",
              }}
              mt={4}
              minWidth={"25vw"}
              height={"40vh"}
            >
              <Typography fontSize={"1.5rem"} color="#cac9ff" fontWeight={700}>
                Your Result
              </Typography>
              <Box
                height={"150px"}
                sx={{
                  background:
                    "linear-gradient(180deg, #4d21c9 0%, rgba(37, 33, 201, 0) 100%, rgba(37, 33, 201, 0) 100%)",
                }}
                color={"white"}
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                width={"150px"}
                borderRadius={"50%"}
              >
                <Typography fontWeight={700} fontSize={"2.6rem"}>
                  {markData?.totalMarks}
                </Typography>
                <Typography>of {markData?.totalScore}</Typography>
                {markData?.pendingMarks > 0 && (
                  <Typography fontSize={"0.75rem"} textAlign={"center"} px={1}>
                    {markData.pendingMarks} awaiting review
                  </Typography>
                )}
              </Box>
              {(() => {
                const percentage =
                  (markData.totalMarks / markData.totalScore) * 100;
                let feedback;

                if (percentage < 50) {
                  feedback = "Needs Improvement";
                } else if (percentage < 70) {
                  feedback = "Good";
                } else if (percentage < 90) {
                  feedback = "Very Good";
                } else {
                  feedback = "Excellent";
                }

                return (
                  <Typography
                    fontSize={"1.5rem"}
                    fontWeight={600}
                    color={"#cac9ff"}
                  >
                    {feedback}
                  </Typography>
                );
              })()}
            </Box>
            <Button
              onClick={handleContinue}
              startIcon={<ReplayIcon />}
              sx={{
                borderRadius: 3,
                p: 2,
                textTransform: "none",
                fontSize: "1.2rem",
              }}
              variant="outlined"
            >
              Practice again
            </Button>
            <Box
              mt={4}
              width={"100%"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              flexDirection={"column"}
              gap={5}
              mb={12}
            >
              {markData?.feedBack
                ?.sort((a, b) => a.questionNumber - b.questionNumber) // Sort by questionNumber
                .map((data, index) => {
                  const answerData = markData.answers.find(
                    (answer) => answer.questionId === data.id
                  );
                  // Optional: a feedback row whose question no longer resolves
                  // (question edited or removed between attempt and review)
                  // used to throw here and blank the entire results screen.
                  const questionData = questions.find(
                    (question) => question._id === data.id
                  )?.text;

                  return (
                    <Box
                      key={index}
                      borderRadius={5}
                      width={"60%"}
                      sx={{
                        overflow: "hidden",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <Box
                        borderBottom={"1px solid lightgrey"}
                        sx={{
                          background:
                            data.pending || data.type === "descriptive"
                              ? "#edf0f5"
                              : answerData?.isCorrect
                              ? "#d4edda"
                              : "#f8d7da",
                        }}
                      >
                        <Box
                          color={"#88909d"}
                          fontWeight={600}
                          px={2}
                          pt={2}
                          display={"flex"}
                          justifyContent={"space-between"}
                        >
                          <Box
                            display={"flex"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            p={1}
                            gap={1}
                            sx={{
                              background:
                                data.pending || data.type === "descriptive"
                                  ? "grey"
                                  : answerData?.isCorrect
                                  ? "#14bb14"
                                  : "#ff6060",
                              color: "white",
                              borderRadius: 3,
                            }}
                          >
                            <Typography>
                              {data.pending ? "Awaiting review" : "Mark"}
                            </Typography>
                            <Typography>
                              {data.pending
                                ? `—/${data.outOfMark}`
                                : `${data.mark}/${data.outOfMark}`}
                            </Typography>
                          </Box>

                          <Typography>
                            {data.questionNumber}/{questions.length}
                          </Typography>
                        </Box>
                        <Box py={1} px={4}>
                          <Typography
                            fontSize={"1.3rem"}
                            // color={"#88909d"}
                            color={"black"}
                            dangerouslySetInnerHTML={{
                              __html: questionData || "Question text",
                            }}
                          />
                        </Box>
                      </Box>
                      <Box pb={1} mt={2} px={4}>
                        <Typography>Your Answer</Typography>
                        <Typography
                          fontSize={"1rem"}
                          color={"#88909d"}
                          dangerouslySetInnerHTML={{
                            __html:
                              data.submittedAnswer?.descriptiveAnswer ||
                              // Not keyed on `type`: it is absent on stored
                              // submissions (the Answer schema drops it), so
                              // keying on it showed a blank "Your Answer"
                              // for anything read back from the database.
                              formatSubmittedAnswer(data.submittedAnswer),
                          }}
                        ></Typography>
                      </Box>
                      <Box pb={2} px={4}>
                        <Typography>
                          {data.submittedAnswer?.type === "descriptive"
                            ? "Feedback"
                            : "Correct Answer"}
                        </Typography>
                        {data.submittedAnswer?.type === "descriptive" &&
                        Array.isArray(data.correctAnswer) ? (
                          data.correctAnswer.length > 0 ? (
                            data.correctAnswer.map((point, idx) => (
                              <Typography
                                key={idx}
                                my={1}
                                fontSize={"1rem"}
                                color={"#88909d"}
                              >
                                {idx + 1}.{point}
                              </Typography>
                            ))
                          ) : (
                            <Typography
                              my={1}
                              fontSize={"1rem"}
                              color={"#88909d"}
                            >
                              —
                            </Typography>
                          )
                        ) : (
                          <Typography fontSize={"1rem"} color={"#88909d"}>
                            {formatAnswer(data.correctAnswer)}
                          </Typography>
                        )}
                      </Box>
                      {data.feedback &&
                        data.feedback !== "" && (
                          <Box pb={1} mt={2} px={4}>
                            <Typography fontWeight={600}>Feedback</Typography>
                            <Typography
                              fontSize={"1rem"}
                              color={"#88909d"}
                              dangerouslySetInnerHTML={{
                                __html: data.feedback,
                              }}
                            ></Typography>
                          </Box>
                        )}
                    </Box>
                  );
                })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AssessmentSection;
